import debounce from '../util/debounce';
import delegate from '../util/delegate';

// keycodes
const UP    = 38;
const DOWN  = 40;
const ESC   = 27;
const ENTER = 13;

export default class AutoComplete {

  constructor( opts ) {
    Object.assign( this, opts, {
      index: -1,
      debouncedInput: debounce( this.onInput.bind( this ), 200 )
    });

    if ( !( this.input instanceof HTMLInputElement ) ) {
      throw new TypeError('input must be an HTMLInputElement');
    }

    if ( !( this.list instanceof HTMLUListElement ) ) {
      throw new TypeError('list must be an HTMLUListElement');
    }

    this.bindEvents();
  }

  // set up initial event bindings
  bindEvents() {
    this.input.addEventListener( 'input', this.debouncedInput );
    this.input.addEventListener( 'keyup', this.onKeyUp.bind( this ) );
    this.input.addEventListener( 'keydown', this.onKeyDown.bind( this ) );
    this.input.addEventListener( 'blur', this.onBlur.bind( this ) );
    delegate( this.list, 'li', 'mouseover', this.onMouseOver.bind( this ) );
    delegate( this.list, 'li', 'click', this.onClick.bind( this ) );
  }

  // handle input events (search and show results)
  onInput( ev ) {
    if ( this.hooks && typeof this.hooks.input === 'function' ) {
      this.hooks.input( ev );
    }

    const input = ev.target.value;

    if ( !input ) {
      this.list.style.display = 'none';
      return;
    }

    this.loadTimeout = setTimeout( () => this.onLoading(), 100 );

    clearTimeout( this.blurTimeout );

    this
    .search( input )
    .then( results => this.suggest( results, input ) )
    .catch( console.error.bind( console ) );
  }

  // handle loading
  onLoading() {
    const next = this.input.nextElementSibling;

    if ( next && next.classList.contains('loading') ) {
      next.classList.add('active');
    }
  }

  // handle loading complete
  onLoaded() {
    const next = this.input.nextElementSibling;

    if ( next && next.classList.contains('loading') ) {
      next.classList.remove('active');
    }
  }

  // handle keyup events (blur on Escape key)
  onKeyUp( ev ) {
    if ( this.hooks && typeof this.hooks.keyup === 'function' ) {
      this.hooks.keyup( ev );
    }

    if ( ev.keyCode === ESC ) {
      this.input.value = '';
      this.input.blur();
      this.list.style.display = 'none';
    }
  }

  // handle keydown events (arrow key vavigation + select with Enter key)
  onKeyDown( ev ) {
    if ( this.hooks && typeof this.hooks.keydown === 'function' ) {
      this.hooks.keydown( ev );
    }

    const code = ev.keyCode;
    const curr = this.list.children[ this.index ];

    // we have results and the user is navigating through them
    if ( this.list.children.length && code === DOWN || code === UP ) {
      const inc    = code === DOWN ? 1 : -1;
      const target = this.list.children[ this.index + inc ];

      if ( target ) {
        if ( curr ) {
          this.list.children[ this.index ].classList.remove('focus');
        }
        this.index += inc;
        target.classList.add('focus');
      }
      ev.preventDefault();
    // the user hit Enter
    } else if ( code === ENTER ) {
      ev.preventDefault();
      // they're selecting something
      if ( curr ) {
        const e = document.createEvent('Event');
        e.initEvent( 'click', true, true );
        curr.dispatchEvent( e );
      // nothing selected yet? highlight the first item
      } else {
        const target = this.list.children[ this.index = 0 ];
        target.classList.add('focus');
      }
    }
  }

  // handle blur events (hide list after delay)
  onBlur( ev ) {
    if ( this.hooks && typeof this.hooks.blur === 'function' ) {
      this.hooks.blur( ev );
    }

    this.blurTimeout = setTimeout( () => {
      this.list.style.display = 'none';
    }, 250 );
  }

  // handle mouseover events (simulate :hover styles)
  // we need to do this in JS so that arrow key highlighting and hover
  // highlighting don't both activate at different elements at the same time
  onMouseOver( ev, target ) {
    if ( this.hooks && typeof this.hooks.mouseover === 'function' ) {
      this.hooks.mouseover( ev );
    }

    const curr     = this.list.querySelector('.focus');
    const children = Array.prototype.slice.call( this.list.children );
    const index    = children.indexOf( target );

    if ( curr ) {
      curr.classList.remove('focus');
    }

    this.index = index;
    target.classList.add('focus');
  }

  // respond to suggestion clicks
  onClick( ev, target ) {
    if ( this.hooks && typeof this.hooks.click === 'function' ) {
      this.hooks.click( ev );
    }

    const suggestion = JSON.parse( target.dataset.suggestion );

    this.list.style.display = 'none';

    // temporarily unbind our handler so that `onSelect` can update the
    // input's value without triggering a new search
    this.input.removeEventListener( 'input', this.debouncedInput );
    this.onSelect( suggestion );
    this.input.addEventListener( 'input', this.debouncedInput );
  }

  // show suggestions
  suggest( suggestions /*, input */ ) {
    const frag = document.createDocumentFragment();

    clearTimeout( this.loadTimeout );
    this.onLoaded();

    if ( !suggestions.length ) {
      return;
    }

    for ( let suggestion of suggestions ) {
      const result = this.renderSuggestion( suggestion );

      let li;

      if ( result instanceof HTMLLIElement ) {
        li = result;
      } else {
        li = document.createElement('li');
        li.innerHTML = result;
      }

      li.setAttribute( 'data-suggestion', JSON.stringify( suggestion ) );
      frag.appendChild( li );
    }

    this.index = -1;
    this.list.innerHTML = '';
    this.list.appendChild( frag );
    this.list.style.display = 'block';
  }

  // the following methods must be provided by an implementation...

  // accepts a string (the user's input) and returns a promise
  // that resolves with an array of suggestions
  search() {
    throw new Error('subclasses or instances must override `search`');
  }

  // accepts a suggestion (however that's defined by `search`) and returns
  // an HTML string for that item
  renderSuggestion() {
    throw new Error('subclasses or instances must override `renderSuggestion`');
  }

  // accepts a suggestion that the user has selected
  onSelect() {
    throw new Error('subclasses or instances must override `onSelect`');
  }

}
